import { useGetCurrentUserRolesWithTagsQuery, useGetUserRolesWithTagsByIdQuery } from 'store/services/core/user';
import { useMemo, useState } from 'react';

import type { RoleWithTags, TagsGroupedRoles } from 'types';

interface UseUserRolesWithTagsParams {
  absentUserId?: string;
}

interface UseUserRolesWithTagsResult {
  rolesData: RoleWithTags[];
  tagOptions: Array<{ value: string; label: string }>;
  tagsGroupedRoles: TagsGroupedRoles;
  selectedRoleIds: string[];
  isLoading: boolean;
  error: unknown;
  setSelectedTags: (tags: string[]) => void;
}

export const useUserRolesWithTags = ({ absentUserId }: UseUserRolesWithTagsParams): UseUserRolesWithTagsResult => {
  const [selectedTags, setSelectedTagsState] = useState<string[]>([]);

  const { data, isLoading, error } = useGetCurrentUserRolesWithTagsQuery({}, { skip: !!absentUserId });
  const {
    data: absentUserRolesData,
    isLoading: isLoadingAbsentUserRoles,
    error: errorAbsentUserRoles,
  } = useGetUserRolesWithTagsByIdQuery({ id: absentUserId || '' }, { skip: !absentUserId });

  const rolesData = useMemo(
    () => (absentUserId ? absentUserRolesData?.items : data?.items) || [],
    [absentUserId, absentUserRolesData?.items, data?.items]
  );

  const tagsGroupedRoles = useMemo(() => {
    const grouped: TagsGroupedRoles = {};

    rolesData.forEach((role) => {
      role.tags.forEach((tag) => {
        if (!grouped[tag]) {
          grouped[tag] = [];
        }
        grouped[tag].push(role);
      });
    });

    return grouped;
  }, [rolesData]);

  const tagOptions = useMemo(() => {
    const distinctTags = Array.from(new Set(rolesData.flatMap((role) => role.tags)));
    return distinctTags.map((tag) => ({
      value: tag,
      label: tag,
    }));
  }, [rolesData]);

  const selectedRoleIds = useMemo(() => {
    if (selectedTags.length === 0) return [];

    const roleIds = new Set<string>();

    selectedTags.forEach((tag) => {
      const rolesForTag = tagsGroupedRoles[tag] || [];
      rolesForTag.forEach((role) => {
        roleIds.add(role.id);
      });
    });

    return Array.from(roleIds);
  }, [selectedTags, tagsGroupedRoles]);

  return {
    rolesData,
    tagOptions,
    tagsGroupedRoles,
    selectedRoleIds,
    isLoading: absentUserId ? isLoadingAbsentUserRoles : isLoading,
    error: absentUserId ? errorAbsentUserRoles : error,
    setSelectedTags: (tags: string[]) => {
      setSelectedTagsState(tags);
    },
  };
};
